import { AllHostsList, DenmarkHostModel, SocialChannel } from "@/database";
import spriteImage from "../../assets/icons/spriteImage.png";
import { socialPublishStatusToTextAndColor } from "../ui/conversions";
import { DENMARK_ROTATING_STATUS } from "@/utils/constants/uiConstants";
import { store } from "@/store";

const DEFAULT_LOGO_POSITION = "-738px -10px";

export const SOCIAL_DOMAIN_MAPPER: Record<number, string> = {
  153: "https://economictimes.indiatimes.com/",
  83: "https://timesofindia.indiatimes.com/",
  373: "https://timesxpgujarati.indiatimes.com/",
  377: "https://timesxpmalayalam.indiatimes.com/",
  374: "https://timesxphindi.indiatimes.com/",
  379: "https://timesxptelugu.indiatimes.com/",
  376: "https://timesxpmarathi.indiatimes.com/",
  375: "https://timesxpkannada.indiatimes.com/",
  378: "https://timesxptamil.indiatimes.com/",
  372: "https://timesxpbengali.indiatimes.com/",
  261: "https://www.businessinsider.in/",
  252: "https://eisamay.com/",
  53: "https://navbharattimes.indiatimes.com/",
  155: "https://marathi.indiatimes.com/",
  245: "https://vijaykarnataka.com/",
  285: "https://tamil.samayam.com/",
  286: "https://telugu.samayam.com/",
  287: "https://malayalam.samayam.com/",
  338: "https://www.iamgujarat.com/",
};

export const DENMARK_IMAGES_MAPPER: Record<number, string> = {
  83: "https://static.toiimg.com/",
  153: "https://img.etimg.com/",
};

export const HOST_LIST_MAPPER: Record<number, string> = {
  "43": "Test",
  "53": "NBT- Online",
  "83": "TOI",
  "19": "ET",
  "153": "ET-hfi",
  "155": "MT- Online",
  "183": "Times Now",
  "245": "Indiatimes Vijay Karnataka",
  "248": "ZoomTV",
  "252": "eisamay",
  "261": "ETEnergy",
  "263": "ETRetail",
  "265": "ETAuto",
  "266": "ETHealth",
  "268": "ETTelecom",
  "269": "ETBFSI",
  "271": "ETCIO",
  "281": "ETBrandEquity",
  "282": "ETReal Estate",
  "285": "Tamil",
  "286": "Telugu",
  "287": "Malayalam",
  "301": "ETCFO",
  "314": "ETCISO",
  "329": "ETGovernment",
  "338": "iamgujarat",
  "341": "ETHospitalityWorld",
  "342": "ETHRWorld",
  "346": "ETTravelWorld",
  "352": "Gadget Central",
  "353": "TimesNow - Navbharat",
  "355": "Times Foodie",
  "357": "ET Infra",
  "359": "ETCISOSEA",
  "360": "ETHRME",
  "361": "ETHRSEA",
  "363": "RadioMirchi",
  "371": "Zoom Hindi",
  "372": "Bengali TimesXP",
  "373": "Gujarati TimesXP",
  "374": "Hindi TimesXP",
  "375": "Kannada TimesXP",
  "376": "Marathi TimesXP",
  "377": "Malayalam TimesXP",
  "378": "Tamil TimesXP",
  "379": "Telugu TimesXP",
  "382": "ET Now",
  "383": "TimesXP-English",
  "677": "ET Gujrati",
  "3812890": "Willo Parent",
  "388": "SPEAKTINGTREEHINDI",
  "292": "newspoint",
  "307": "Gadgets now",
};

export const getDenmarkMediaUrlWithSeopath = async (
  detailId: string,
  hostId: number,
  dispatch: any,
  callDenmarkDetailApi: any,
  isArticle = false,
  callDenmarkArticleGetApi?: any,
  allHostsList?: AllHostsList
): Promise<string | null> => {
  try {
    let seopath: string | null = null;

    if (isArticle && callDenmarkArticleGetApi) {
      seopath = await new Promise<string | null>((resolve) => {
        dispatch(
          callDenmarkArticleGetApi({ detailId, hostId }, (response: any) => {
            if (response.status === 200 && response.data) {
              resolve(response.data.seopath || response.data.seo_path || null);
            } else {
              console.error(
                "Failed to get seopath from Article API:",
                response.error
              );
              resolve(null);
            }
          })
        );
      });
    } else {
      seopath = await new Promise<string | null>((resolve) => {
        dispatch(
          callDenmarkDetailApi({ detailId, hostId }, (response: any) => {
            if (response.status === 200 && response.data) {
              resolve(response.data);
            } else {
              console.error(
                "Failed to get seopath from CMS API:",
                response.error
              );
              resolve(null);
            }
          })
        );
      });
    }

    if (seopath) {
      let baseUrl = "";

      // Use allHostsList if present and has hosts, otherwise use SOCIAL_DOMAIN_MAPPER
      if (allHostsList?.hosts && allHostsList.hosts.length > 0) {
        const host = allHostsList.hosts.find((h) => h.hostId === hostId);
        baseUrl = host?.domain || "";
      } else {
        baseUrl = SOCIAL_DOMAIN_MAPPER[hostId] || "";
      }

      // Ensure baseUrl ends with /
      if (baseUrl && !baseUrl.endsWith("/")) {
        baseUrl += "/";
      }

      if (isArticle) {
        return `${baseUrl}${seopath}`;
      } else {
        return `${baseUrl}${seopath}/videoshow/${detailId}.cms`;
      }
    }
    return null;
  } catch (error) {
    console.error("Error getting Denmark media URL:", error);
    return null;
  }
};

export const getHostLogoStyleByHostId = (
  hostId: string | number | undefined,
  hosts?: any[],
  hostOptions?: any[],
  denmarkHosts?: Array<{
    hostId?: number | string;
    hostName?: string | null;
    denmarkActive?: boolean;
  }>
) => {
  if (!hostId) {
    return {};
  }

  // First priority: Check hostOptions (already has computed logoStyle)
  const hostMatch = hostOptions?.find(
    (host: any) => host.value === hostId?.toString()
  );
  if (hostMatch?.logoStyle) {
    return hostMatch.logoStyle;
  }

  // Second priority: Check hosts array
  const hostFromList = hosts?.find(
    (host: any) => host.id === hostId?.toString()
  );
  if (hostFromList?.name) {
    return getHostLogoStyle(hostFromList.name);
  }

  // Third priority: Check denmarkHosts
  if (denmarkHosts && denmarkHosts.length > 0) {
    const numericHostId = Number(hostId);
    if (!Number.isNaN(numericHostId)) {
      const fromRedux = denmarkHosts.find((h) => {
        if (!h) return false;
        const candidate = h.hostId;
        if (candidate === undefined || candidate === null) return false;
        return candidate && candidate === numericHostId;
      });
      if (fromRedux?.hostName) {
        return getHostLogoStyle(fromRedux.hostName);
      }
    }
  }

  // Fourth priority: Fallback to HOST_LIST_MAPPER
  const hostName = HOST_LIST_MAPPER[hostId as keyof typeof HOST_LIST_MAPPER];
  if (hostName) {
    return getHostLogoStyle(hostName);
  }

  return {};
};

export const getHostLogoStyle = (hostname: string) => {
  const spriteWidth = 32;
  const spriteHeight = 32;

  const denmarkHostsList = store.getState().constants.denmarkHostsList
    .list as DenmarkHostModel[];
  const denmarkHost = denmarkHostsList?.find(
    (host) => host.name?.toLowerCase() === hostname?.toLowerCase()
  );

  const position = denmarkHost?.lg_position || DEFAULT_LOGO_POSITION;

  return {
    backgroundImage: `url(${spriteImage})`,
    backgroundPosition: position,
    width: `${spriteWidth}px`,
    height: `${spriteHeight}px`,
    display: "inline-block",
    zoom: "0.7",
    transformOrigin: "left top",
    minWidth: "32px",
  };
};

/**
 * Creates host options array with logo styles for use in dropdowns/selects
 * @param hosts - Array of host objects with id and name
 * @param denmarkHosts - Optional array of Denmark hosts from Redux for fallback names
 * @returns Array of host options with value, label, and logoStyle
 */
export const createHostOptions = (
  hosts?: Array<{ id: string; name?: string }>,
  denmarkHosts?: Array<{
    hostId?: number | string;
    hostName?: string | null;
    denmarkActive?: boolean;
  }>
): Array<{
  value: string;
  label: string;
  logoStyle: React.CSSProperties;
}> => {
  if (!hosts || hosts.length === 0) {
    return [];
  }

  return hosts
    .map((host: { id: string; name?: string }) => {
      const rawName = typeof host.name === "string" ? host.name : "";

      // If host has a valid name, use it
      if (rawName.trim() !== "") {
        return {
          value: host.id,
          label: rawName,
          logoStyle: getHostLogoStyle(rawName),
        };
      }

      // Fallback: Check denmarkHosts for the name
      if (Array.isArray(denmarkHosts)) {
        const matched = denmarkHosts.find(
          (dh) => String(dh?.hostId).trim() === String(host.id).trim()
        );
        const matchedName =
          typeof matched?.hostName === "string" ? matched.hostName.trim() : "";

        if (matchedName !== "") {
          return {
            value: host.id,
            label: matchedName,
            logoStyle: getHostLogoStyle(matchedName),
          };
        }
      }

      // If no valid name found, return null to filter out
      return null;
    })
    .filter(Boolean) as Array<{
    value: string;
    label: string;
    logoStyle: React.CSSProperties;
  }>;
};

export const getRandomColor = (): string => {
  const r = Math.floor(Math.random() * 256);
  const g = Math.floor(Math.random() * 256);
  const b = Math.floor(Math.random() * 256);
  const alpha = 0.8;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** A social publish item as it appears on a media entry / publish chip. */
interface SocialItemForImage {
  job_type?: string;
  channel_name?: string;
  platform?: string;
  image?: string;
}

const withImageSizeSuffix = (url: string, suffix: string): string =>
  url.replace(/(\.(?:jpe?g|png|webp|gif))(\?.*)?$/i, `${suffix}$1$2`);

interface GetSocialChannelImageOptions {
  size?: "small" | "large";
  sizeSuffix?: string;
}

export const getSocialChannelImage = (
  item: SocialItemForImage | undefined | null,
  channels: SocialChannel[] | undefined | null,
  options: "small" | "large" | GetSocialChannelImageOptions = {}
): string | null => {
  if (!item) return null;

  const { size = "small", sizeSuffix } =
    typeof options === "string" ? { size: options } : options;

  const applySuffix = (url: string) =>
    sizeSuffix ? withImageSizeSuffix(url, sizeSuffix) : url;

  const matchedChannel = channels?.find(
    (channel) =>
      channel.name === item.channel_name && channel.platform === item.job_type
  );
  if (matchedChannel) {
    if (matchedChannel.platform === "facebook") {
      return `https://graph.facebook.com/${matchedChannel.id}/picture?type=${size}`;
    }
    if (matchedChannel.image) return applySuffix(matchedChannel.image);
  }

  if (item.image) {
    return item.job_type === "facebook"
      ? `https://graph.facebook.com/${item.platform}/picture?type=${size}`
      : applySuffix(item.image);
  }

  return null;
};

export const CONST_SOURCE_MAPPER = "dbsync";

export const CONST_SOCIAL_THUMB_SIZE = ".jpg";
export const THUMBNAIL_SIZE_SUFFIX = "_180";

export const applyThumbnailSize = (
  url: string | undefined | null,
  suffix: string = THUMBNAIL_SIZE_SUFFIX
): string => (url ? withImageSizeSuffix(url, suffix) : "");

export const getConsistentColor = (seed: string): string => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  const r = Math.abs(hash) % 256;
  const g = Math.abs(hash >> 8) % 256;
  const b = Math.abs(hash >> 16) % 256;

  const alpha = 0.8;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const generateThumbnailUrl = (
  articleId: string,
  hostId?: number
): string => {
  if (!hostId) {
    return "";
  } else if (!DENMARK_IMAGES_MAPPER[hostId]) {
    return "https://static.langimg.com/photo/" + articleId + ".cms";
  }
  const baseUrl = DENMARK_IMAGES_MAPPER[hostId];
  return `${baseUrl}photo/${articleId}.cms`;
};

export const getDenmarkStatusIndicatorData = (status: number) => {
  const statusData = socialPublishStatusToTextAndColor(status);

  if (DENMARK_ROTATING_STATUS.includes(status)) {
    return {
      type: "rotating",
      className: "rotating-sandbox",
      style: {
        display: "inline-block",
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        backgroundColor: "#6c757d",
        animation: "rotate 2s infinite linear",
      },
    };
  }

  return {
    type: "static",
    className: `rounded-circle d-inline-block ${statusData.statusColor}`,
    style: {
      height: "10px",
      width: "10px",
      flex: "0 0 10px",
      maxWidth: "10px",
    },
  };
};

export function getAllowedTeamIds(
  userPermissions: string[] | undefined | null
): string[] {
  if (!userPermissions?.length) return [];
  const seen = new Set<string>();
  userPermissions.forEach((perm) => {
    if (typeof perm !== "string" || !perm.trim()) return;
    const parts = perm.split(".");
    if (parts.length < 4) return;
    const [, product, service, level] = parts;
    const serviceOk = service === "cms" || service === "*";
    const levelOk = level === "w" || level === "*";
    if (serviceOk && levelOk && product !== "*") seen.add(product);
  });
  return [...seen];
}

export const REQUIRED_FILTERS = [
  "Media ID",
  "Title & description",
  "Published Destinations",
  "Business",
  "Team/Property",
  "Agency",
  "Agency Group",
  "Content Status",
  "Upload Date",
  "Modified Date",
  "Relationship Type",
  "Media Duration",
  "Content Category",
  "Content Type",
  "Content Source",
  "Uploader",
  "Modifier",
];
